<template>
  <div ref="container"
       v-click-outside="onClickOutside"
       v-bind=$attrs
       v-on="myListeners"
       :class="$style.container"
       :disabled="disabled"
       :error="error"
       :filled="filled"
       :focus="focus"
       :style="containerStyle"
       :suggested="suggested && focus">
    <div :class="$style.innerContainer"
         :style="innerContainerStyle">
      <div ref="fixedPluginLeft" :style="{ height: '100%' }">
        <slot name="fixed-plugin-left" />
      </div>
      <div ref="inputContainer"
           :class="$style.inputContainer"
           :disabled="disabled"
           :error="error"
           :filled="filled"
           :focus="focus"
           :style="computedInputContainerStyle"
           :suggested="suggested && focus">
        <div :style="{ height: '100%' }">
          <slot name="plugin-left" />
        </div>
        <input v-model="input"
               :class="$style.input"
               :disabled="disabled"
               :error="error"
               :filled="filled"
               :placeholder="placeholder"
               :style="computedInputStyle"
               :suggested="suggested && focus"
               :type="inputType"
               @input="e => $emit('input', input)"
               @focusin="e => { focus = true; }"
               @focusout="e => { focus = false; }"/>
        <div :style="{ height: '100%' }">
          <slot name="plugin-right" />
        </div>
      </div>
      <div ref="clearButton" :style="centralizer">
        <template v-if="showClearButton">
          <div :style="centralizer">
            <div :style="clearButtonStyle"
                 @click="$emit('clear')">
              <slot name="clear-button">
                &#10005;
              </slot>
            </div>
          </div>
        </template>
        <template v-if="error">
          <div :style="{...centralizer, justifyContent: 'center', ...errorSymbolStyle }">
            <slot name="error-symbol">
              <div :style="{ height: '100%' }">
                &#33;
              </div>
            </slot>
          </div>
        </template>
        <template v-if="lock">
          <div :style="{...centralizer, justifyContent: 'center' }">
            <slot name="lock-symbol" />
          </div>
        </template>
      </div>
      <div ref="fixedPluginRight" :style="{ height: '100%' }">
        <slot name="fixed-plugin-right" />
      </div>
    </div>
    <div ref="popover"
         v-if="suggested">
      <slot name="popover" />
    </div>
  </div>
</template>

<script>
  import ClickOutside from 'vue-click-outside'
  import { DefaultPseudoClassAttributeNames,
           DefaultPseudoClassVariableNames,
           getPseudoClassesObject,
           filterObjectByArray} from '@js/vue-style-utils'

  export default {
    name: 'BaseInputText',
    inheritAttrs: false,
    model: {
      prop: 'vmodelValue',
      event: 'input',
    },
    directives: {
      ClickOutside,
    },
    props: {
      vmodelValue: { type: String },
      clearButtonStyle: { type: Object, required: false, default: () => {
        return { cursor: 'pointer', margin: '0 8px' };
      }},
      disabled: { type: Boolean, required: false, default: false },
      error: { type: Boolean, required: false, default: false },
      errorSymbolStyle: { type: Object, required: false, default: () => {
        return {
          backgroundColor: '#D72638', borderRadius: '7px',
          color: '#FFFFFF', cursor: 'default',
          fontSize: '10px', fontWeight: 'bold', height: '14px',
          margin: '0 4px', width: '14px'
        };
      }},
      filled: { type: Boolean, required: false, default: false },
      inputContainerStyle: { type: Object, required: false, default: () => {} },
      inputStyle: { type: Object, required: false, default: () => {} },
      inputType: { type: String, required: false, default: 'text' },
      lock: { type: Boolean, required: false, default: false },
      placeholder: { type: String, required: false, default: 'Enter' },
      suggested: { type: Boolean, required: false, default: false },
    },
    data: function () {
      return {
        centralizer: { display: 'flex', alignItems: 'center', height: '100%' },
        computedInputStyle: {
          backgroundColor: 'transparent',
          border: 'none',
          display: 'block',
          height: '100%',
          outline: 'none',
          whiteSpace: 'nowrap',
          width: '100%',
        },
        innerContainerStyle: {
          alignItems: 'flex-start',
          display: 'flex',
        },
        input: '',
        inputContainerWidth: '100%',
        inputContainerHeight: '100%',
        initInputContainerHeightDiff: 0,
        focus: false,
        height: '32px',
        scrollbarHeight: 0,
        popoverHeight: 0,
        postfixes: ['', 'hover', 'filled', 'focus', 'suggested', 'disabled', 'error'],
      };
    },
    created: function () {
      this.height = this.$attrs.height || this.height;
      this.input = this.vmodelValue;

      let classObject = null;
      let attrs = null;

      const postfixes = [...this.postfixes, 'placeholder'];
      classObject = getPseudoClassesObject(
        this.inputStyle, DefaultPseudoClassAttributeNames,
        '--base-input-text-input-', DefaultPseudoClassVariableNames, postfixes);
      attrs = filterObjectByArray(
        this.inputStyle, DefaultPseudoClassAttributeNames);

      this.computedInputStyle = {
        ...this.computedInputStyle,
        ...attrs,
        ...classObject,
      };

      classObject = getPseudoClassesObject(
        this.$attrs.innerContainerStyle, DefaultPseudoClassAttributeNames,
        '--base-input-text-inner-container-', DefaultPseudoClassVariableNames,
        this.postfixes);
      attrs = filterObjectByArray(
        this.$attrs.innerContainerStyle, DefaultPseudoClassAttributeNames);

      this.innerContainerStyle = {
        ...this.innerContainerStyle,
        height: this.height,
        ...attrs,
        ...classObject,
      };
    },
    mounted: function () {
      this.inputContainerHeight = this.$refs.inputContainer.parentElement.offsetHeight;
      const inputContainerOffsetHeight = this.$refs.inputContainer.offsetHeight;
      const inputContainerClientHeight = this.$refs.inputContainer.clientHeight;
      this.initInputContainerHeightDiff = inputContainerOffsetHeight - inputContainerClientHeight;
      this.updateInputContainerWidth();
    },
    updated: function () {
      this.updateScrollbarHeight();
      this.updateInputContainerWidth();
      this.updatePopoverHeight();
    },
    computed: {
      myListeners: function () {
        const { input, ...listeners } = this.$listeners;
        return listeners;
      },
      showClearButton: function () {
        return !this.disabled && (this.input.length > 0 || this.filled);
      },
      containerStyle: function () {
        const classObject = getPseudoClassesObject(
          this.$attrs, [...DefaultPseudoClassAttributeNames, 'width'],
          '--base-input-text-container-', [...DefaultPseudoClassVariableNames, 'width'],
          this.postfixes);
        const attrs = filterObjectByArray(
          this.$attrs, [...DefaultPseudoClassAttributeNames, 'width']);

        return {
          boxSizing: 'border-box',
          display: 'inline-box',
          ...attrs,
          ...classObject,
          height: (parseInt(this.height) + parseInt(this.popoverHeight)) + 'px',
        };
      },
      computedInputContainerStyle: function () {
        const attributeNames = ['margin', 'padding'];
        const variableNames = ['margin', 'padding'];
        const classObject = getPseudoClassesObject(
          this.inputContainerStyle, attributeNames,
          '--base-input-text-input-container-', variableNames,
          this.postfixes);
        const attrs = filterObjectByArray(
          this.inputContainerStyle, attributeNames);

        return {
          overflowX: 'auto',
          display: 'flex',
          alignItems: 'center',
          height: this.inputContainerHeight + this.scrollbarHeight + 'px',
          width: this.inputContainerWidth,
          ...attrs,
          ...classObject,
        };
      },
    },
    methods: {
      onClickOutside: function () {
        this.$emit('click-outside');
      },
      updateScrollbarHeight: function () {
        if (!this.$refs.inputContainer) {
          return;
        }
        const inputContainerOffsetHeight = this.$refs.inputContainer.offsetHeight;
        const inputContainerClientHeight = this.$refs.inputContainer.clientHeight;
        const inputContainerHeightDiff = inputContainerOffsetHeight - inputContainerClientHeight;
        this.scrollbarHeight = inputContainerHeightDiff - this.initInputContainerHeightDiff;
      },
      updateInputContainerWidth: function () {
        if (!this.$refs.container) {
          this.inputContainerWidth = '100%';
        }

        const fixedPluginLeftWidth = this.$refs.fixedPluginLeftWidth ? this.$refs.fixedPluginLeftWidth.offsetWidth : 0;
        const fixedPluginRightWidth = this.$refs.fixedPluginRightWidth ? this.$refs.fixedPluginRightWidth.offsetWidth : 0;
        const clearButtonWidth = this.$refs.clearButton ? this.$refs.clearButton.offsetWidth : 0;
        this.inputContainerWidth = this.$refs.container.offsetWidth - fixedPluginLeftWidth - fixedPluginRightWidth - clearButtonWidth;
      },
      updatePopoverHeight: function () {
        this.popoverHeight = this.$refs.popover ? this.$refs.popover.offsetHeight : 0;
      },
    },
    watch: {
      vmodelValue: function (newVal, oldVal) {
        this.input = newVal;
      },
      scrollbarHeight: function (newVal, oldVal) {
        this.$emit('scrollbar-change', newVal);
      },
      popoverHeight: function (newVal, oldVal) {
        this.$emit('popover-change', newVal);
      },
    },
  };
</script>

<style lang="stylus" module>
  .container
    background-color var(--base-input-text-container-background-color)
    border var(--base-input-text-container-border)
    border-radius var(--base-input-text-container-border-radius)
    border-width var(--base-input-text-container-border-width)
    box-shadow var(--base-input-text-container-box-shadow)
    width var(--base-input-text-container-width)
    &:hover
      background-color var(--base-input-text-container-background-color-hover)
      border var(--base-input-text-container-border-hover)
      border-radius var(--base-input-text-container-border-radius-hover)
      border-width var(--base-input-text-container-border-width-hover)
      box-shadow var(--base-input-text-container-box-shadow-hover)
      width var(--base-input-text-container-width-hover)
    &[disabled]
      background-color var(--base-input-text-container-background-color-disabled)
      border var(--base-input-text-container-border-disabled)
      border-radius var(--base-input-text-container-border-radius-disabled)
      border-width var(--base-input-text-container-border-width-disabled)
      box-shadow var(--base-input-text-container-box-shadow-disabled)
      width var(--base-input-text-container-width-disabled)
    &[error]
      background-color var(--base-input-text-container-background-color-error)
      border var(--base-input-text-container-border-error)
      border-radius var(--base-input-text-container-border-radius-error)
      border-width var(--base-input-text-container-border-width-error)
      box-shadow var(--base-input-text-container-box-shadow-error)
      width var(--base-input-text-container-width-error)
    &[filled]
      background-color var(--base-input-text-container-background-color-filled)
      border var(--base-input-text-container-border-filled)
      border-radius var(--base-input-text-container-border-radius-filled)
      border-width var(--base-input-text-container-border-width-filled)
      box-shadow var(--base-input-text-container-box-shadow-filled)
      width var(--base-input-text-container-width-filled)
    &[focus]
      background-color var(--base-input-text-container-background-color-focus)
      border var(--base-input-text-container-border-focus)
      border-radius var(--base-input-text-container-border-radius-focus)
      border-width var(--base-input-text-container-border-width-focus)
      box-shadow var(--base-input-text-container-box-shadow-focus)
      width var(--base-input-text-container-width-focus)
    &[suggested]
      background-color var(--base-input-text-container-background-color-suggested)
      border var(--base-input-text-container-border-suggested)
      border-radius var(--base-input-text-container-border-radius-suggested)
      border-width var(--base-input-text-container-border-width-suggested)
      box-shadow var(--base-input-text-container-box-shadow-suggested)
      width var(--base-input-text-container-width-suggested)

  .innerContainer
    background-color var(--base-input-text-inner-container-background-color)
    border var(--base-input-text-inner-container-border)
    border-radius var(--base-input-text-inner-container-border-radius)
    border-width var(--base-input-text-inner-container-border-width)
    box-shadow var(--base-input-text-inner-container-box-shadow)
    &:hover
      background-color var(--base-input-text-inner-container-background-color-hover)
      border var(--base-input-text-inner-container-border-hover)
      border-radius var(--base-input-text-inner-container-border-radius-hover)
      border-width var(--base-input-text-inner-container-border-width-hover)
      box-shadow var(--base-input-text-inner-container-box-shadow-hover)
    &[disabled]
      background-color var(--base-input-text-inner-container-background-color-disabled)
      border var(--base-input-text-inner-container-border-disabled)
      border-radius var(--base-input-text-inner-container-border-radius-disabled)
      border-width var(--base-input-text-inner-container-border-width-disabled)
      box-shadow var(--base-input-text-inner-container-box-shadow-disabled)
    &[error]
      background-color var(--base-input-text-inner-container-background-color-error)
      border var(--base-input-text-inner-container-border-error)
      border-radius var(--base-input-text-inner-container-border-radius-error)
      border-width var(--base-input-text-inner-container-border-width-error)
      box-shadow var(--base-input-text-inner-container-box-shadow-error)
    &[filled]
      background-color var(--base-input-text-inner-container-background-color-filled)
      border var(--base-input-text-inner-container-border-filled)
      border-radius var(--base-input-text-inner-container-border-radius-filled)
      border-width var(--base-input-text-inner-container-border-width-filled)
      box-shadow var(--base-input-text-inner-container-box-shadow-filled)
    &[focus]
      background-color var(--base-input-text-inner-container-background-color-focus)
      border var(--base-input-text-inner-container-border-focus)
      border-radius var(--base-input-text-inner-container-border-radius-focus)
      border-width var(--base-input-text-inner-container-border-width-focus)
      box-shadow var(--base-input-text-inner-container-box-shadow-focus)
    &[suggested]
      background-color var(--base-input-text-inner-container-background-color-suggested)
      border var(--base-input-text-inner-container-border-suggested)
      border-radius var(--base-input-text-inner-container-border-radius-suggested)
      border-width var(--base-input-text-inner-container-border-width-suggested)
      box-shadow var(--base-input-text-inner-container-box-shadow-suggested)

  .inputContainer
    margin var(--base-input-text-input-container-margin)
    padding var(--base-input-text-input-container-padding)
    &:hover
      margin var(--base-input-text-input-container-margin-hover)
      padding var(--base-input-text-input-container-padding-hover)
    &[disabled]
      margin var(--base-input-text-input-container-margin-disabled)
      padding var(--base-input-text-input-container-padding-disabled)
    &[error]
      margin var(--base-input-text-input-container-margin-error)
      padding var(--base-input-text-input-container-padding-error)
    &[focus]
      margin var(--base-input-text-input-container-margin-focus)
      padding var(--base-input-text-input-container-padding-focus)
    &[suggested]
      margin var(--base-input-text-input-container-margin-suggested)
      padding var(--base-input-text-input-container-padding-suggested)

  .input
    color var(--base-input-text-input-color)
    font-family var(--base-input-text-input-font-family)
    font-size var(--base-input-text-input-font-size)
    font-weight var(--base-input-text-input-font-weight)
    &:hover
      color var(--base-input-text-input-color-hover)
      font-family var(--base-input-text-input-font-family-hover)
      font-size var(--base-input-text-input-font-size-hover)
      font-weight var(--base-input-text-input-font-weight-hover)
    &:focus
      color var(--base-input-text-input-color-focus)
      font-family var(--base-input-text-input-font-family-focus)
      font-size var(--base-input-text-input-font-size-focus)
      font-weight var(--base-input-text-input-font-weight-focus)
    &[disabled]
      color var(--base-input-text-input-color-disabled)
      font-family var(--base-input-text-input-font-family-disabled)
      font-size var(--base-input-text-input-font-size-disabled)
      font-weight var(--base-input-text-input-font-weight-disabled)
    &[suggested]
      color var(--base-input-text-input-color-suggested)
      font-family var(--base-input-text-input-font-family-suggested)
      font-size var(--base-input-text-input-font-size-suggested)
      font-weight var(--base-input-text-input-font-weight-suggested)
    &::placeholder
      color var(--base-input-text-input-color-placeholder)
      font-family var(--base-input-text-input-font-family-placeholder)
      font-size var(--base-input-text-input-font-size-placeholder)
      font-weight var(--base-input-text-input-font-weight-placeholder)
</style>
